import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { formatNewsMonthYear } from "@/lib/displayOrderDates";
import { normalizeExternalHref } from "@/lib/entitySocialLinks";

const INITIAL_VISIBLE = 12;
const LOAD_MORE = 12;

type BranchRef = { name: string; slug: string } | null;
type BatchRef = {
  slug: string;
  custom_title: string | null;
  official_title: string | null;
  al_year: number | null;
} | null;
type AssociationRef = { name: string; slug: string } | null;

type ProjectRow = {
  id: string;
  title: string;
  description: string | null;
  image_url: string | null;
  link_url: string | null;
  created_at: string | null;
  updated_at: string | null;
  branch: BranchRef;
  batch: BatchRef;
  association: AssociationRef;
};

function one<T>(x: T | T[] | null | undefined): T | null {
  if (x == null) return null;
  return Array.isArray(x) ? (x[0] ?? null) : x;
}

function batchLabel(b: NonNullable<BatchRef>) {
  return b.custom_title || b.official_title || "Batch";
}

function parentBadge(p: ProjectRow): { label: string; href: string } | null {
  if (p.branch) {
    return {
      label: p.branch.name,
      href: `/alumni/branches/${p.branch.slug}`,
    };
  }
  if (p.batch) {
    return {
      label: batchLabel(p.batch),
      href: `/alumni/batches/${p.batch.slug}`,
    };
  }
  if (p.association) {
    return {
      label: p.association.name,
      href: `/alumni/associations/${p.association.slug}`,
    };
  }
  return { label: "OBA", href: "/about" };
}

export function ProjectsListPage() {
  const [rows, setRows] = useState<ProjectRow[]>([]);
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);
  const loadSentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (visibleCount >= rows.length || rows.length === 0) return;

    const el = loadSentinelRef.current;
    if (!el) return;

    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisibleCount((c) => Math.min(c + LOAD_MORE, rows.length));
        }
      },
      { rootMargin: "240px 0px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [visibleCount, rows.length]);

  useEffect(() => {
    void supabase
      .from("projects")
      .select(
        `
        id,
        title,
        description,
        image_url,
        link_url,
        created_at,
        updated_at,
        branch:branches(name, slug),
        batch:batches(slug, custom_title, official_title, al_year),
        association:associations(name, slug)
      `
      )
      .is("branch_id", null)
      .is("batch_id", null)
      .is("association_id", null)
      .order("updated_at", { ascending: false })
      .then(({ data }) => {
        const list = (data ?? []) as {
          id: string;
          title: string;
          description: string | null;
          image_url: string | null;
          link_url: string | null;
          created_at: string | null;
          updated_at: string | null;
          branch: BranchRef | BranchRef[];
          batch: BatchRef | BatchRef[];
          association: AssociationRef | AssociationRef[];
        }[];
        setRows(
          list
            .map((r) => ({
              id: r.id,
              title: r.title,
              description: r.description,
              image_url: r.image_url,
              link_url: r.link_url,
              created_at: r.created_at,
              updated_at: r.updated_at,
              branch: one(r.branch),
              batch: one(r.batch),
              association: one(r.association),
            }))
        );
        setVisibleCount(INITIAL_VISIBLE);
      });
  }, []);

  const visibleRows = rows.slice(0, visibleCount);
  const hasMore = visibleCount < rows.length;

  return (
    <div className="mx-auto max-w-7xl px-4 py-14">
      <p className="text-xs font-bold uppercase tracking-[0.3em] text-brand-maroon">
        Our work
      </p>
      <h1 className="mt-2 font-display text-4xl font-bold text-brand-blue">
        OBA Projects
      </h1>
      <p className="mt-4 text-brand-blue/85">
        Site-wide initiatives led by the Old Boys’ Association.
      </p>

      {rows.length === 0 ? (
        <p className="mt-10 text-sm text-brand-blue/60">
          No projects published yet.
        </p>
      ) : (
        <>
          <ul className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {visibleRows.map((p) => {
              const badge = parentBadge(p);
              const projectMonth = formatNewsMonthYear(p.updated_at ?? p.created_at);
              const safeLink = normalizeExternalHref(p.link_url);
              return (
                <li
                  key={p.id}
                  className="flex h-full flex-col overflow-hidden rounded-2xl border border-brand-gray bg-brand-gray/30"
                >
                  {badge ? (
                    <div className="border-b border-brand-gray/40 px-5 pt-4">
                      <Link
                        to={badge.href}
                        className="text-xs font-bold uppercase tracking-wide text-brand-maroon hover:underline"
                      >
                        {badge.label}
                      </Link>
                    </div>
                  ) : null}
                  <Link
                    to={`/projects/${p.id}`}
                    className="flex min-h-0 flex-1 flex-col text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-maroon focus-visible:ring-offset-2"
                    aria-label={p.title ? `View project: ${p.title}` : "View project"}
                  >
                    {p.image_url && (
                      <img
                        src={p.image_url}
                        alt=""
                        loading="lazy"
                        className="h-44 w-full shrink-0 object-cover"
                      />
                    )}
                    <div className="flex min-h-0 flex-1 flex-col p-5">
                      {projectMonth ? (
                        <p className="text-xs font-semibold uppercase tracking-wide text-brand-maroon/90">
                          {projectMonth}
                        </p>
                      ) : null}
                      <h2 className="mt-1 font-display text-xl font-bold text-brand-blue">{p.title}</h2>
                      {p.description && (
                        <p className="mt-2 line-clamp-4 text-sm text-brand-blue/85 whitespace-pre-line">
                          {p.description}
                        </p>
                      )}
                    </div>
                  </Link>
                  {safeLink ? (
                    <div className="border-t border-brand-gray/40 px-5 py-3">
                      <a
                        href={safeLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-bold text-brand-maroon hover:underline"
                      >
                        Open external link →
                      </a>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
          {hasMore ? (
            <div
              ref={loadSentinelRef}
              className="h-px w-full shrink-0"
              aria-hidden
            />
          ) : null}
        </>
      )}
    </div>
  );
}
